import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import styles from './styles.module.css';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const TIERS = [
  { min: 50, max: 1000, price: 2.85 },
  { min: 1001, max: 5000, price: 2.26 },
  { min: 5001, max: 10000, price: 1.88 },
  { min: 10001, max: 50000, price: 1.48 },
  { min: 50001, max: 99999, price: 1.13 },
  { min: 100000, max: Infinity, price: 0.94 },
];
const MIN_VOL = 50;
const MAX_VOL = 150000;
const MIN_LOG = Math.log10(MIN_VOL);
const MAX_LOG = Math.log10(MAX_VOL);
const MANUAL_PRICE = 10;
const TICK_BREAKPOINTS = [1000, 5000, 10000, 50000, 100000];

function volumeToPos(v) {
  const lv = Math.log10(Math.max(MIN_VOL, v));
  return Math.round(((lv - MIN_LOG) / (MAX_LOG - MIN_LOG)) * 1000);
}
function posToVolume(p) {
  const lv = MIN_LOG + (MAX_LOG - MIN_LOG) * (p / 1000);
  return Math.round(Math.pow(10, lv));
}
function getTier(v) {
  return TIERS.find((t) => v >= t.min && v <= t.max) || TIERS[TIERS.length - 1];
}
function tierRangeLabel(tier, fmtInt) {
  return tier.max === Infinity ? `${fmtInt(tier.min)}+` : `${fmtInt(tier.min)} – ${fmtInt(tier.max)}`;
}

export default function ROI() {
  const { t, i18n } = useTranslation('landing');
  const lang = i18n.language;

  const fmtInt = useMemo(() => {
    const formatter = new Intl.NumberFormat(lang);
    return (n) => formatter.format(Math.round(n));
  }, [lang]);
  const fmtMoney = useMemo(() => {
    const formatter = new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (n) => formatter.format(n);
  }, [lang]);

  const manualItems = t('roi.manual.items', { returnObjects: true });
  const voxItems = t('roi.vox.items', { returnObjects: true });

  const [volume, setVolume] = useState(MIN_VOL);

  const { tier, sliderPos, fillPercent, savingsPercent, packageTotal, ticks } = useMemo(() => {
    const tier = getTier(volume);
    const sliderPos = volumeToPos(volume);
    const savingsPercent = Math.round((1 - tier.price / MANUAL_PRICE) * 100);
    const packageTotal = volume * tier.price;
    const ticks = TICK_BREAKPOINTS.map((bp) => ({
      pos: ((Math.log10(bp) - MIN_LOG) / (MAX_LOG - MIN_LOG)) * 100,
      label: fmtInt(bp),
    }));
    return { tier, sliderPos, fillPercent: sliderPos / 10, savingsPercent, packageTotal, ticks };
  }, [volume, fmtInt]);

  const handleSliderInput = (e) => {
    setVolume(posToVolume(Number(e.target.value)));
  };

  return (
    <motion.section
      id="roi"
      className={styles.section}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={fadeUp}
    >
      <div className={styles.container}>
        <div className={styles.center}>
          <span className={styles.tag}>{t('roi.tag')}</span>
          <h2 className={styles.title}>{t('roi.title')}</h2>
          <p className={styles.subtitle}>{t('roi.subtitle')}</p>
        </div>

        {/* Price / volume box */}
        <div className={styles.priceBox}>
          <div className={styles.priceBoxHeader}>
            <div>
              <div className={styles.volumeNumber}>
                {fmtInt(volume)} <span className={styles.volumeUnit}>{t('roi.volumeUnit')}</span>
              </div>
            </div>
            <div className={styles.tierCol}>
              <div className={styles.tierPill}>
                <span className={styles.tierPillLabel}>{t('roi.tierPill')}</span>
                <span className={styles.tierPillValue}>
                  {tierRangeLabel(tier, fmtInt)} {t('roi.tierUnit')}
                </span>
              </div>
              <div className={styles.tierRate}>
                {t('roi.tierRatePrefix')} <strong>${fmtMoney(tier.price)} {t('roi.tierRateUnit')}</strong>
              </div>
            </div>
          </div>

          <div className={styles.sliderTrackWrap}>
            <div className={styles.sliderTrackBg} />
            <div className={styles.sliderTrackFill} style={{ width: `${fillPercent}%` }} />
            {ticks.map((tick, i) => (
              <div key={i} className={styles.sliderTick} style={{ left: `${tick.pos}%` }} />
            ))}
            <input
              id="vox-roi-slider"
              className={styles.sliderInput}
              type="range"
              min={0}
              max={1000}
              value={sliderPos}
              onChange={handleSliderInput}
            />
          </div>

          <div className={styles.tickLabelsWrap}>
            {ticks.map((tick, i) => (
              <div key={i} className={styles.tickLabel} style={{ left: `${tick.pos}%` }}>
                {tick.label}
              </div>
            ))}
          </div>

          <div className={styles.rangeLimits}>
            <span>{fmtInt(MIN_VOL)}</span>
            <span>{fmtInt(MAX_VOL)}+</span>
          </div>

          <div className={styles.priceBoxFooter}>
            <div className={styles.minPurchaseNote}>{t('roi.minPurchase')}</div>
            <div className={styles.packageEstimate}>
              {t('roi.packageEstimate')} <strong>${fmtMoney(packageTotal)} USD</strong>
            </div>
          </div>
        </div>

        {/* Comparison cards */}
        <div className={styles.grid}>
          <div className={styles.card}>
            <div className={styles.label}>{t('roi.manual.label')}</div>
            <div className={styles.price}>{t('roi.manual.price')}</div>
            <div className={styles.sub}>{t('roi.manual.sub')}</div>
            <div className={styles.items}>
              {manualItems.map((text, i) => (
                <div key={i} className={styles.item}>
                  <span className={styles.x}>✗</span>
                  {text}
                </div>
              ))}
            </div>
          </div>

          <div className={`${styles.card} ${styles.dark}`}>
            <div className={styles.savingsBadge}>{t('roi.savingsBadge', { percent: savingsPercent })}</div>
            <div className={`${styles.label} ${styles.labelLight}`}>{t('roi.vox.label')}</div>
            <div className={styles.dynamicBadge}>{t('roi.vox.dynamicBadge')}</div>
            <div className={styles.priceRow}>
              <div className={`${styles.price} ${styles.priceGood}`}>${fmtMoney(tier.price)}</div>
              <div className={styles.priceUnit}>{t('roi.vox.priceUnit')}</div>
            </div>
            <div className={styles.tierRangeNote}>{t('roi.vox.sub')}</div>
            <div className={styles.items}>
              {voxItems.map((text, i) => (
                <div key={i} className={`${styles.item} ${styles.itemLight}`}>
                  <span className={styles.check}>✓</span>
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Callout */}
        <div className={styles.infoBox}>
          <p>{t('roi.callout.line1')}</p>
          <p className={styles.infoBoxStrong}>{t('roi.callout.line2')}</p>
          <p>
            {t('roi.callout.line3a')} <strong>{t('roi.callout.line3b')}</strong> {t('roi.callout.line3c')}
          </p>
        </div>

        {/* Final CTA */}
        <div className={styles.finalCta}>
          <p className={styles.finalCtaText}>{t('roi.finalCta.question')}</p>
          <Link to="cta" smooth duration={500} offset={-64} className={styles.ctaButton}>
            {t('roi.finalCta.button')}
          </Link>
        </div>
      </div>
    </motion.section>
  );
}